/* ==========================================================================
   BELLE AURA — couche de mouvement
   --------------------------------------------------------------------------
   Trois comportements, tous facultatifs : si ce script ne démarre pas, la
   page reste complètement lisible et utilisable.

     1. Révélations au défilement  (IntersectionObserver)
     2. Relief 3D des fiches au curseur
     3. Drapeau « page défilée » pour l'en-tête en verre dépoli

   Règles respectées :
     • `prefers-reduced-motion: reduce` coupe 1 et 2, et suit les changements
       de réglage en direct (pas seulement au chargement).
     • Le relief ne s'active que sur pointeur fin — jamais au tap.
     • Toute écriture de style passe par requestAnimationFrame.
     • La première section n'est jamais masquée : le héros doit s'afficher
       immédiatement (pas de pénalité LCP, pas d'écran vide).
     • Filet de sécurité : tout élément encore masqué au bout de 3 s est
       révélé d'office. Une animation ratée reste un détail ; du contenu
       invisible serait une panne.
   ========================================================================== */

(function () {
  'use strict';

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------------------------------------------------------------------
     1. Révélations au défilement
     --------------------------------------------------------------------- */
  function setupReveals() {
    var main = document.getElementById('MainContent');
    if (!main || !('IntersectionObserver' in window)) return;

    var sections = Array.prototype.slice.call(main.querySelectorAll('.shopify-section'));
    if (!sections.length) return;

    var targets = [];

    sections.forEach(function (section, index) {
      // Le héros (première section) reste visible d'emblée.
      if (index === 0) return;

      // Dans une grille de produits, ce sont les fiches qui entrent en
      // cascade ; ailleurs, c'est la section entière qui se pose.
      var cards = section.querySelectorAll('.product-card, .resource-card, .collection-card');

      if (cards.length > 1) {
        Array.prototype.forEach.call(cards, function (card, i) {
          card.classList.add('ba-reveal');
          // Cascade plafonnée : au-delà de 8 fiches, l'attente deviendrait
          // perceptible comme une lenteur plutôt que comme une intention.
          card.style.setProperty('--ba-reveal-delay', Math.min(i, 8) * 60 + 'ms');
          targets.push(card);
        });
      } else {
        section.classList.add('ba-reveal');
        targets.push(section);
      }
    });

    if (!targets.length) return;

    function reveal(el) {
      el.classList.add('is-in');
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      // Déclenche un peu avant l'entrée réelle : le mouvement est terminé
      // quand l'œil arrive dessus.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });

    // Filet de sécurité. Un élément placé dans un conteneur que l'observateur
    // ne sait pas suivre (carrousel, overflow masqué, content-visibility)
    // n'intersecte jamais et resterait à opacité 0. Au bout de 3 s on révèle
    // tout ce qui reste, sans condition.
    var safety = window.setTimeout(function () {
      targets.forEach(reveal);
      observer.disconnect();
    }, 3000);

    return function teardown() {
      window.clearTimeout(safety);
      observer.disconnect();
      targets.forEach(function (el) {
        el.classList.remove('ba-reveal');
        el.classList.remove('is-in');
        el.style.removeProperty('--ba-reveal-delay');
      });
    };
  }

  /* ---------------------------------------------------------------------
     2. Relief 3D des fiches
     --------------------------------------------------------------------- */
  function setupTilt() {
    if (!finePointer.matches) return;

    var cards = document.querySelectorAll('.product-card, .resource-card');
    if (!cards.length) return;

    var maxRaw = getComputedStyle(document.documentElement)
      .getPropertyValue('--ba-tilt-max');
    var max = parseFloat(maxRaw) || 5;
    if (max === 0) return;

    var bound = [];

    Array.prototype.forEach.call(cards, function (card) {
      card.classList.add('ba-tilt');

      var frame = null;
      var rect = null;

      function onEnter() {
        // Mesuré une seule fois à l'entrée : évite un reflow par mouvement.
        rect = card.getBoundingClientRect();
        card.setAttribute('data-ba-tilting', '');
      }

      function onMove(event) {
        if (!rect) return;
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = null;
          // Position du curseur ramenée dans [-0.5, 0.5]
          var px = (event.clientX - rect.left) / rect.width - 0.5;
          var py = (event.clientY - rect.top) / rect.height - 0.5;
          // L'axe X s'incline à l'inverse du curseur : la carte « regarde »
          // le pointeur au lieu de le fuir.
          card.style.setProperty('--ba-tilt-x', (-py * max).toFixed(2) + 'deg');
          card.style.setProperty('--ba-tilt-y', (px * max).toFixed(2) + 'deg');
        });
      }

      function onLeave() {
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = null;
        }
        rect = null;
        card.removeAttribute('data-ba-tilting');
        card.style.setProperty('--ba-tilt-x', '0deg');
        card.style.setProperty('--ba-tilt-y', '0deg');
      }

      card.addEventListener('pointerenter', onEnter);
      card.addEventListener('pointermove', onMove);
      card.addEventListener('pointerleave', onLeave);
      // Un clic emmène ailleurs : on remet la carte à plat pour qu'elle ne
      // reste pas figée de travers au retour arrière.
      card.addEventListener('click', onLeave);

      bound.push(function () {
        card.removeEventListener('pointerenter', onEnter);
        card.removeEventListener('pointermove', onMove);
        card.removeEventListener('pointerleave', onLeave);
        card.removeEventListener('click', onLeave);
        onLeave();
        card.classList.remove('ba-tilt');
      });
    });

    return function teardown() {
      bound.forEach(function (fn) {
        fn();
      });
    };
  }

  /* ---------------------------------------------------------------------
     3. Drapeau « page défilée » — en-tête en verre dépoli
     Ce comportement ne relève pas du confort visuel : il reste actif même
     en mouvement réduit, car il sert la lisibilité de l'en-tête.
     --------------------------------------------------------------------- */
  function setupScrollFlag() {
    var root = document.documentElement;
    var ticking = false;

    function apply() {
      ticking = false;
      if (window.scrollY > 24) {
        root.setAttribute('data-ba-scrolled', '');
      } else {
        root.removeAttribute('data-ba-scrolled');
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Orchestration
     --------------------------------------------------------------------- */
  var teardownReveals = null;
  var teardownTilt = null;

  function startDecorativeMotion() {
    if (motionQuery.matches) return;
    teardownReveals = setupReveals() || null;
    teardownTilt = setupTilt() || null;
  }

  function stopDecorativeMotion() {
    if (teardownReveals) {
      teardownReveals();
      teardownReveals = null;
    }
    if (teardownTilt) {
      teardownTilt();
      teardownTilt = null;
    }
  }

  function init() {
    setupScrollFlag();
    startDecorativeMotion();
  }

  // Le réglage système peut changer en cours de session : on suit.
  var onMotionChange = function () {
    stopDecorativeMotion();
    if (!motionQuery.matches) {
      startDecorativeMotion();
    }
  };

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionChange);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(onMotionChange); // Safari < 14
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Le thème re-rend des sections sans recharger la page (filtres, éditeur,
  // pagination) : on réarme le mouvement sur le contenu neuf.
  document.addEventListener('shopify:section:load', function () {
    stopDecorativeMotion();
    startDecorativeMotion();
  });
})();
